/**
 * Shared date parsing/formatting utilities.
 * All dates in this app are DD-MM-YYYY (Excel/SharePoint exports).
 */

/** Parse a date string into a Date object. Supports DD-MM-YYYY and YYYY-MM-DD formats. Returns null on failure. */
export function parseDate(dateStr) {
  if (!dateStr) return null;
  
  // Try YYYY-MM-DD (ISO-like) first — unambiguous because year is always 4 digits at start
  const isoMatch = String(dateStr).match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (isoMatch) {
    return new Date(parseInt(isoMatch[1], 10), parseInt(isoMatch[2], 10) - 1, parseInt(isoMatch[3], 10));
  }
  
  // Try DD-MM-YYYY — day/month are 1-2 digits, year is 4 digits at end
  const dmyMatch = String(dateStr).match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dmyMatch) {
    return new Date(parseInt(dmyMatch[3], 10), parseInt(dmyMatch[2] - 1), parseInt(dmyMatch[1], 10));
  }
  
  // Fallback to native parser for any other format
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
}

/** Format a date string to DD-MM-YYYY (e.g. "02/08/2026"). */
export function formatDateDDMMYYYY(dateStr) {
  const d = parseDate(dateStr);
  if (!d || isNaN(d.getTime())) return '\u2014';
  return d.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Format a date string to "2 Aug 2026". */
export function formatDateReadable(dateStr) {
  const d = parseDate(dateStr);
  if (!d || isNaN(d.getTime())) return '\u2014';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** Return a Date object with today's date, time zeroed to midnight. */
export function getTodayMidnight() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

// ---------------------------------------------------------------------------
// Qualification status helpers (date-driven, replaces isCurrent flag)
// ---------------------------------------------------------------------------

/** Resolve a qualification's effective start date.
  *  Priority: qual.commencementDate → earliest unit.activityStartDate → null */
export function resolveQualStartDate(qual) {
  if (qual && typeof qual.commencementDate === 'string') {
    const d = parseDate(qual.commencementDate);
    if (d) return d;
  }
  if (Array.isArray(qual?.units) && qual.units.length > 0) {
    const starts = qual.units.map(u => parseDate(u.activityStartDate)).filter(Boolean);
    if (starts.length > 0) return new Date(Math.min(...starts));
  }
  return null;
}

/** Resolve a qualification's effective end date.
  *  Priority: qual.expectedCompletionDate → latest unit.activityEndDate → null */
export function resolveQualEndDate(qual) {
  if (qual && typeof qual.expectedCompletionDate === 'string') {
    const d = parseDate(qual.expectedCompletionDate);
    if (d) return d;
  }
  if (Array.isArray(qual?.units) && qual.units.length > 0) {
    const ends = qual.units.map(u => parseDate(u.activityEndDate)).filter(Boolean);
    if (ends.length > 0) return new Date(Math.max(...ends));
  }
  return null;
}

/** Determine the status of a qualification relative to today. */
export function getQualStatus(qual, today) {
  const start = resolveQualStartDate(qual);
  const end = resolveQualEndDate(qual);

  // No dates at all → not_stated
  if (!start && !end) return 'not_stated';

  // Start is in the future (or null but we have an end, meaning it's already past start)
  if (start && start > today) return 'not_started';

  // End date exists and has passed → ended
  if (end && end < today) return 'ended';

  // Started and not yet ended → active
  return 'active';
}

/** Find the current qualification from a list.
 *  - Active quals: pick the one with the LATEST commencementDate (most recently started).
 *  - Fallback: most recent by end date, then by start date. */
export function getCurrentQualification(quals, today) {
  if (!Array.isArray(quals) || quals.length === 0) return null;

  const active = quals.filter(q => getQualStatus(q, today) === 'active');
  if (active.length > 0) {
    // Pick the one with the latest commencementDate (most recently started)
    return [...active].sort((a, b) => {
      const startA = resolveQualStartDate(a);
      const startB = resolveQualStartDate(b);
      if (!startA && !startB) return 0;
      if (!startA) return 1;
      if (!startB) return -1;
      return startB - startA; // descending: latest first
    })[0];
  }

  // Fallback: most recent by end date (descending), then by start date
  const all = [...quals].sort((a, b) => {
    const endA = resolveQualEndDate(a);
    const endB = resolveQualEndDate(b);
    if (endB && endA) return endB - endA; // descending: latest end first
    if (!endB && !endA) { /* fall through to start */ } else if (!endB) return 1; else return -1;

    const startA = resolveQualStartDate(a);
    const startB = resolveQualStartDate(b);
    if (startB && startA) return startB - startA; // descending: latest start first
    if (!startB) return 1;
    if (!startA) return -1;
    return 0;
  });

  return all[0] || quals[0];
}

/** Build a display label + colour for a qualification's status.
 *  Always resolves dates through resolveQualStartDate/resolveQualEndDate
 *  (which parse the string fields first) so the result is always a Date. */
export function getQualStatusInfo(qual, today) {
  const status = getQualStatus(qual, today);
  if (status === 'active') return { label: 'Active', color: '#27ae60' };
  if (status === 'ended') {
    const endDate = resolveQualEndDate(qual);
    return { label: `Ended ${endDate ? endDate.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}`, color: '#5b6776' };
  }
  if (status === 'not_started') {
    const startDate = resolveQualStartDate(qual);
    return { label: `Starts ${startDate ? startDate.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''}`, color: '#5b6776' };
  }
  return { label: 'Not yet stated', color: '#5b6776' };
}
